import { useState, useEffect, useCallback, useRef } from 'react';
import { useLazyPollEntitlementsQuery } from '../../services/entitlementsApi';

const INITIAL_DELAY_MS = 1000; // 1 second before polling starts
const POLLING_INTERVAL_MS = 5000; // poll every 5 seconds
const POLLING_TIMEOUT_MS = 31000; // stop polling after 31 seconds (allows 6 polls)

type EntitlementsData = ReturnType<typeof useLazyPollEntitlementsQuery>[1]['data'];

type UseEntitlementsPollingResult = {
  startPolling: () => void;
  pollingComplete: boolean;
};

// Provides manual entitlements polling controls for callers that need to wait for fulfillment completion.
const useEntitlementsPolling = (currentPlanCode: string): UseEntitlementsPollingResult => {
  const [pollingActive, setPollingActive] = useState<boolean>(false);
  const [pollingComplete, setPollingComplete] = useState<boolean>(false);

  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nextPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingActiveRef = useRef<boolean>(false);

  // Checks whether the latest entitlements response confirms the requested fulfillment has completed.
  const isFulfillmentComplete = (data: EntitlementsData, currentPlanCode: string): boolean => {
    const hasDeferment = Boolean(data?.defermentInfo);
    const hasPlanChanged = data?.plans?.some((plan: { planCode?: string }) => plan.planCode !== currentPlanCode) ?? false;

    return hasDeferment || hasPlanChanged;
  };

  const [fetchEntitlements, { data }] = useLazyPollEntitlementsQuery();

  // Stops polling, marks it complete, and clears any pending timers.
  const stopPolling = useCallback(() => {
    pollingActiveRef.current = false;
    setPollingActive(false);
    setPollingComplete(true);
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
    }
    if (nextPollRef.current) {
      clearTimeout(nextPollRef.current);
    }
  }, []);

  // Starts polling after the initial delay and arms the overall polling timeout.
  const startPolling = useCallback(() => {
    setPollingComplete(false);
    pollingActiveRef.current = true;

    initialDelayRef.current = setTimeout(() => {
      setPollingActive(true);

      pollingTimeoutRef.current = setTimeout(() => {
        stopPolling();
      }, POLLING_TIMEOUT_MS);
    }, INITIAL_DELAY_MS);
  }, [stopPolling]);

  // Reacts to cached lazy-query data changes and stops polling when fulfillment is complete.
  useEffect(() => {
    if (!pollingActive || !data) return;

    if (isFulfillmentComplete(data, currentPlanCode)) {
      stopPolling();
    }
  }, [data, pollingActive, stopPolling, currentPlanCode]);

  // Runs the manual polling loop while polling is active.
  useEffect(() => {
    if (!pollingActive) return;

    // Fetches entitlements once, checks completion, then schedules the next poll if needed.
    const pollEntitlements = async () => {
      let polledData: EntitlementsData | undefined;

      try {
        const response = await fetchEntitlements(undefined, false);
        polledData = response.data;
      } catch {
        // Keep polling until completion or timeout to tolerate transient API failures.
      }

      if (!pollingActiveRef.current) return;

      if (polledData && isFulfillmentComplete(polledData, currentPlanCode)) {
        stopPolling();
        return;
      }

      nextPollRef.current = setTimeout(() => {
        pollEntitlements();
      }, POLLING_INTERVAL_MS);
    };

    pollEntitlements();

    return () => {
      if (nextPollRef.current) {
        clearTimeout(nextPollRef.current);
      }
    };
  }, [currentPlanCode, fetchEntitlements, pollingActive, stopPolling]);

  // Clears all timers when the component using this hook unmounts.
  useEffect(() => {
    return () => {
      if (initialDelayRef.current) {
        clearTimeout(initialDelayRef.current);
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
      if (nextPollRef.current) {
        clearTimeout(nextPollRef.current);
      }
    };
  }, []);

  return { startPolling, pollingComplete };
};

export default useEntitlementsPolling;
